import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    Card,
    CardContent,
    CardMedia,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction
} from '@mui/material';
import {
    Close as CloseIcon,
    AttachFile as AttachFileIcon,
    Image as ImageIcon,
    VideoFile as VideoFileIcon,
    Description as DescriptionIcon,
    Download as DownloadIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    PlayArrow as PlayArrowIcon,
    Pause as PauseIcon,
    VolumeUp as VolumeUpIcon,
    VolumeOff as VolumeOffIcon,
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from 'react-oidc-context';
import { $authHost, fileApi } from '../../../http';
import Viewer from 'react-viewer';
import OnlyOfficeEditor from '../../modules/DocumentServerNew';
import FileUploader from './FileUploader';

// Компонент для отображения изображений с превью
const ImagesPreviewSection = ({ images, darkMode, onFileDelete, users }) => {
    console.log(images)
    const auth = useAuth();
    const [visible, setVisible] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    if (!images || images.length === 0) return null;

    // Функция для получения URL изображения для просмотра
    const getStreamImageUrl = (auth, imageId) => {
        const token = auth.user?.access_token;
        return `${$authHost.defaults.baseURL}/apig/s3/files/${imageId}/stream?token=${encodeURIComponent(token)}`;
    };

    const handleImageClick = (index) => {
        setActiveIndex(index);
        setVisible(true);
    };

    return (
        <>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                p: 1.5,
                borderRadius: 2,
                bgcolor: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                border: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)'
            }}>
                <Typography variant="h6" sx={{
                    color: darkMode ? '#e0e0e0' : '#333',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 600,
                    fontSize: '1rem'
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                        border: '1px solid rgba(76, 175, 80, 0.3)'
                    }}>
                        <ImageIcon sx={{ fontSize: '16px', color: '#4caf50' }} />
                    </Box>
                    Изображения
                    <Chip
                        label={images.length}
                        size="small"
                        sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            bgcolor: darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
                            color: darkMode ? '#81c784' : '#4caf50',
                            border: '1px solid rgba(76, 175, 80, 0.3)'
                        }}
                    />
                </Typography>

                <ImageList
                    sx={{
                        width: '100%',
                        p: 0,
                        // height: 250,
                        '& .MuiImageListItem-root': {
                            cursor: 'pointer',
                            borderRadius: 2,
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                // transform: 'scale(1.02)',
                                boxShadow: darkMode
                                    ? '0 8px 24px rgba(0, 0, 0, 0.4)'
                                    : '0 8px 24px rgba(0, 0, 0, 0.15)'
                            }
                        }
                    }}
                    cols={4}
                    rowHeight={160}
                >
                    {images.map((image, index) => (
                        <ImageListItem key={image.id}>
                            <img
                                src={image.preview}
                                alt={image.name || image.key}
                                loading="lazy"
                                onClick={() => handleImageClick(index)}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                            <ImageListItemBar
                                title={image.name || image.key}
                                subtitle={`${users.get(image.user_id)}`}
                                actionIcon={
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onFileDelete(image.id);
                                        }}
                                        sx={{
                                            color: '#ff6b6b',
                                            '&:hover': {
                                                bgcolor: 'rgba(255, 107, 107, 0.1)'
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                }
                                sx={{
                                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)'
                                }}
                            />
                        </ImageListItem>
                    ))}
                </ImageList>
            </Box>

            {visible && (
                <Viewer
                    visible={visible}
                    onClose={() => setVisible(false)}
                    images={images.map(image => ({
                        src: getStreamImageUrl(auth, image.id),
                        alt: image.name || image.key,
                        downloadUrl: getStreamImageUrl(auth, image.id)
                    }))}
                    activeIndex={activeIndex}
                    zIndex={9999}
                />
            )}
        </>
    );
};

// Компонент для отображения видео и документов в виде списка
const FilesListSection = ({ files, title, icon: Icon, darkMode, onFileDelete, onFileClick }) => {
    if (!files || files.length === 0) return null;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
            border: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)'
        }}>
            <Typography variant="h6" sx={{
                color: darkMode ? '#e0e0e0' : '#333',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 600,
                fontSize: '1rem'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: title === 'Видео'
                        ? 'rgba(255, 152, 0, 0.1)'
                        : 'rgba(156, 39, 176, 0.1)',
                    border: title === 'Видео'
                        ? '1px solid rgba(255, 152, 0, 0.3)'
                        : '1px solid rgba(156, 39, 176, 0.3)'
                }}>
                    <Icon sx={{
                        fontSize: '16px',
                        color: title === 'Видео' ? '#ff9800' : '#9c27b0'
                    }} />
                </Box>
                {title}
                <Chip
                    label={files.length}
                    size="small"
                    sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: title === 'Видео'
                            ? (darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)')
                            : (darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)'),
                        color: title === 'Видео'
                            ? (darkMode ? '#ffb74d' : '#ff9800')
                            : (darkMode ? '#ba68c8' : '#9c27b0'),
                        border: title === 'Видео'
                            ? '1px solid rgba(255, 152, 0, 0.3)'
                            : '1px solid rgba(156, 39, 176, 0.3)'
                    }}
                />
            </Typography>

            <List>
                {files.map((file) => (
                    <ListItem
                        key={file.id}
                        onClick={() => onFileClick && onFileClick(file)}
                        sx={{
                            border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                            borderRadius: 2,
                            mb: 0.5,
                            py: 0.5,
                            cursor: onFileClick ? 'pointer' : 'default',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                transform: 'translateX(4px)',
                                boxShadow: darkMode
                                    ? '0 4px 12px rgba(0, 0, 0, 0.2)'
                                    : '0 4px 12px rgba(0, 0, 0, 0.1)'
                            }
                        }}
                    >
                        <ListItemIcon>
                            <Icon size='small' sx={{ color: '#1976d2' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={file.name || file.key}
                        // secondary={`@${file.user}`}
                        />
                        <ListItemSecondaryAction>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onFileDelete(file.id);
                                }}
                                size='small'
                                sx={{
                                    color: '#ff6b6b',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 107, 107, 0.1)'
                                    }
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

// Компонент для добавления файлов
const AddFilesSection = ({ task, onFilesAdded, darkMode }) => {
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const handleFilesChange = async (files) => {
        setUploadedFiles(files);
        // Автоматически сохраняем файлы при загрузке
        if (files.length > 0) {
            await onFilesAdded(files);
            setUploadedFiles([]);
        }
    };

    return (
        <Box sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
            border: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)'
        }}>
            <Typography variant="h6" sx={{
                mb: 1.5,
                color: darkMode ? '#e0e0e0' : '#333',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 600,
                fontSize: '1rem'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'rgba(25, 118, 210, 0.1)',
                    border: '1px solid rgba(25, 118, 210, 0.3)'
                }}>
                    <AddIcon sx={{ fontSize: '16px', color: '#1976d2' }} />
                </Box>
                Добавить файлы к задаче
            </Typography>

            <FileUploader
                files={uploadedFiles}
                onFilesChange={handleFilesChange}
                maxFiles={10}
                maxFileSize={50 * 1024 * 1024} // 50MB
                allowedTypes={['image/*', 'application/pdf', 'text/*', 'video/*', 'audio/*']}
                darkMode={darkMode}
                disabled={false}
                taskId={task?.id}
            />
        </Box>
    );
};

// Функция для получения расширения файла
const getFileExtension = (filename) => {
    if (!filename) return 'txt';
    const ext = filename.split('.').pop().toLowerCase();
    return ext || 'txt';
};

const TaskFilesDialog = ({
    open,
    user,
    users,
    onClose,
    task,
    darkMode = false,
    updateFiles
}) => {
    console.log(task)
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const auth = useAuth();

    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [videoPlayer, setVideoPlayer] = useState({ open: false, file: null });
    const [documentEditor, setDocumentEditor] = useState({ open: false, file: null });

    // Группировка файлов по типам
    const groupedFiles = React.useMemo(() => {
        if (!attachments || attachments.length === 0) {
            return { images: [], videos: [], documents: [] };
        }

        return attachments.reduce((groups, file) => {
            const contentType = file.type || '';
            const fileName = file.name || file.key || '';

            if (contentType.startsWith('image') ||
                /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileName)) {
                groups.images.push(file);
            } else if (contentType.startsWith('video') ||
                /\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i.test(fileName)) {
                groups.videos.push(file);
            } else {
                groups.documents.push(file);
            }

            return groups;
        }, { images: [], videos: [], documents: [] });
    }, [attachments]);

    // Функция для загрузки файлов задачи
    const fetchAttachments = async () => {
        if (!task?.files || task.files.length === 0) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const attachmentPromises = task.files.map(async (file) => {
                try {
                    // 1. Сначала запрашиваем информацию о файле
                    const fileInfo = await fileApi.getFileInfoById(file.id);
                    if (!fileInfo || !fileInfo.success) {
                        return null;
                    }

                    const fileData = {
                        ...file,
                        name: fileInfo.data.key.split('/').pop()
                    };



                    if (file.type === 'image' && file.has_preview) {
                        try {
                            const previewResponse = await fileApi.getFilePreview(file.id);
                            if (previewResponse && previewResponse.success) {
                                fileData.preview = `data:${previewResponse.data.content_type};base64,${previewResponse.data.base64}`;
                            }
                        } catch (previewErr) {
                            console.warn(`Could not load preview for file ${file.id}:`, previewErr);
                            // Продолжаем без preview
                        }
                    }

                    return fileData;
                } catch (err) {
                    console.error(`Error fetching attachment ${file.id}:`, err);
                    return null;
                }
            });

            const attachmentsData = await Promise.all(attachmentPromises);
            const validAttachments = attachmentsData.filter(att => att !== null);
            setAttachments(validAttachments);
        } catch (err) {
            setError('Ошибка загрузки файлов');
            console.error('Error loading attachments:', err);
        } finally {
            setLoading(false);
        }
    };

    // Загрузка информации о файлах
    useEffect(() => {
        if (open && task) {
            fetchAttachments();
        }
    }, [open, task, auth.user?.access_token]);

    // Обработка добавления файлов
    const handleFilesAdded = async (files) => {
        try {
            // Обновляем задачу с новыми файлами
            const updatedFiles = [...(task.files || []), ...files];
            console.log(updatedFiles)
            updateFiles(updatedFiles)
            await fetchAttachments();

        } catch (error) {
            console.error('Ошибка добавления файлов:', error);
            setError('Ошибка добавления файлов: ' + error.message);
        }
    };

    // Обработка удаления файла
    const handleFileDelete = async (fileId) => {
        try {
            // Удаляем файл из S3
            await fileApi.deleteFile(fileId);

            // Обновляем задачу, убирая файл из списка
            const updatedFiles = (task.files || []).filter(id => id !== fileId);
            updateFiles(updatedFiles)
            await fetchAttachments();

        } catch (error) {
            console.error('Ошибка удаления файла:', error);
            setError('Ошибка удаления файла: ' + error.message);
        }
    };

    // Обработка клика на видео
    const handleVideoClick = (file) => {
        setVideoPlayer({ open: true, file });
    };

    // Обработка клика на документ
    const handleDocumentClick = (file) => {
        setDocumentEditor({ open: true, file });
    };

    // Закрытие видео проигрывателя
    const handleCloseVideoPlayer = () => {
        setVideoPlayer({ open: false, file: null });
    };

    // Закрытие редактора документов
    const handleCloseDocumentEditor = () => {
        setDocumentEditor({ open: false, file: null });
    };

    const totalFiles = groupedFiles.images.length + groupedFiles.videos.length + groupedFiles.documents.length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            sx={{ zIndex: 6000 }}
            slotProps={{
                paper: {
                    sx: {
                        bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                        color: darkMode ? '#f5f7fa' : '#333',
                        borderRadius: isMobile ? 0 : 3,
                        boxShadow: darkMode
                            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                            : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        animation: 'slideIn 0.3s ease-out',
                        position: 'relative',
                        zIndex: 6001,
                        '@keyframes slideIn': {
                            from: {
                                opacity: 0,
                                transform: 'scale(0.95)',
                            },
                            to: {
                                opacity: 1,
                                transform: 'scale(1)',
                            },
                        },
                    }
                }
            }}
        >
            <DialogTitle sx={{
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.12)'
                    : '1px solid rgba(0, 0, 0, 0.12)',
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: 'rgba(25, 118, 210, 0.1)',
                        border: '2px solid #1976d2',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                        }
                    }}>
                        <AttachFileIcon sx={{
                            fontSize: '24px',
                            color: '#1976d2'
                        }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{
                            fontWeight: 700,
                            mb: 0.3,
                            color: darkMode ? '#f5f7fa' : '#333',
                            fontSize: '1.1rem'
                        }}>
                            Прикрепленные файлы
                        </Typography>
                        <Typography variant="body2" sx={{
                            color: darkMode ? '#bbb' : '#666',
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <Chip
                                label={task?.title || task?.name || 'Задача'}
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: '0.75rem',
                                    bgcolor: darkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                    color: darkMode ? '#90caf9' : '#1976d2',
                                    border: '1px solid rgba(25, 118, 210, 0.3)'
                                }}
                            />
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={onClose}
                    size="large"
                    sx={{
                        color: darkMode ? '#f5f7fa' : '#666',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            color: '#f44336',
                            transform: 'scale(1.1)',
                            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {loading ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 4,
                        gap: 1.5
                    }}>
                        <CircularProgress size={48} sx={{ color: '#1976d2' }} />
                        <Typography variant="body1" sx={{
                            color: darkMode ? '#bbb' : '#666',
                            fontWeight: 500
                        }}>
                            Загрузка файлов...
                        </Typography>
                    </Box>
                ) : error ? (
                    <Alert
                        severity="error"
                        sx={{
                            m: 2,
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: '1.5rem'
                            }
                        }}
                    >
                        {error}
                    </Alert>
                ) : totalFiles === 0 ? (
                    <Box sx={{
                        p: 2,
                        '& > *:not(:last-child)': {
                            mb: 2
                        }
                    }}>
                        {/* Раздел добавления файлов когда файлов нет */}
                        <AddFilesSection
                            task={task}
                            onFilesAdded={handleFilesAdded}
                            darkMode={darkMode}
                        />

                        {/* Сообщение о том, что файлов нет */}
                        <Box sx={{
                            textAlign: 'center',
                            p: 4,
                            color: darkMode ? '#bbb' : '#666',
                            bgcolor: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                            borderRadius: 2,
                            border: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)'
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                bgcolor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                mx: 'auto',
                                mb: 1.5
                            }}>
                                <AttachFileIcon sx={{
                                    fontSize: 40,
                                    color: darkMode ? '#666' : '#999',
                                    opacity: 0.7
                                }} />
                            </Box>
                            <Typography variant="h6" sx={{
                                mb: 1,
                                color: darkMode ? '#e0e0e0' : '#333',
                                fontWeight: 600
                            }}>
                                Нет прикрепленных файлов
                            </Typography>
                            <Typography variant="body2" sx={{
                                color: darkMode ? '#999' : '#666'
                            }}>
                                Используйте форму выше для добавления файлов к задаче
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{
                        p: 2,
                        '& > *:not(:last-child)': {
                            mb: 2
                        }
                    }}>
                        {/* Раздел добавления файлов в самом верху */}
                        <AddFilesSection
                            task={task}
                            onFilesAdded={handleFilesAdded}
                            darkMode={darkMode}
                        />

                        {/* Изображения с превью */}
                        <ImagesPreviewSection
                            images={groupedFiles.images}
                            darkMode={darkMode}
                            onFileDelete={handleFileDelete}
                            users={users}
                        />

                        {/* Видео в виде списка */}
                        <FilesListSection
                            files={groupedFiles.videos}
                            title="Видео"
                            icon={VideoFileIcon}
                            darkMode={darkMode}
                            onFileDelete={handleFileDelete}
                            onFileClick={handleVideoClick}
                        />

                        {/* Документы в виде списка */}
                        <FilesListSection
                            files={groupedFiles.documents}
                            title="Документы"
                            icon={DescriptionIcon}
                            darkMode={darkMode}
                            onFileDelete={handleFileDelete}
                            onFileClick={handleDocumentClick}
                        />
                    </Box>
                )}
            </DialogContent>

            {/* Диалог видео проигрывателя */}
            <Dialog
                open={videoPlayer.open}
                onClose={handleCloseVideoPlayer}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                sx={{ zIndex: 7000 }}
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                            color: darkMode ? '#f5f7fa' : '#333',
                            borderRadius: isMobile ? 0 : 3,
                            boxShadow: darkMode
                                ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                                : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        }
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                    color: darkMode ? '#e0e0e0' : '#333',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : '1px solid rgba(0, 0, 0, 0.12)',
                    py: 1.5,
                    px: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255, 152, 0, 0.1)',
                            border: '1px solid rgba(255, 152, 0, 0.3)'
                        }}>
                            <VideoFileIcon sx={{ fontSize: '18px', color: '#ff9800' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {videoPlayer.file?.name}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleCloseVideoPlayer}
                        sx={{
                            color: darkMode ? '#f5f7fa' : '#666',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                bgcolor: 'rgba(244, 67, 54, 0.1)',
                                color: '#f44336',
                                transform: 'scale(1.1)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{
                    p: 0,
                    bgcolor: darkMode ? '#1a1a1a' : '#fff'
                }}>
                    {videoPlayer.file && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 400,
                            p: 2
                        }}>
                            <video
                                controls
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    width: '100%'
                                }}
                                src={`${$authHost.defaults.baseURL}/apig/s3/files/${videoPlayer.file.id}/stream?token=${encodeURIComponent(auth.user?.access_token)}`}
                            >
                                Ваш браузер не поддерживает видео.
                            </video>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Редактор документов OnlyOffice */}
            {documentEditor.open && documentEditor.file && (
                <OnlyOfficeEditor
                    open={documentEditor.open}
                    onClose={handleCloseDocumentEditor}
                    doc={{
                        id: `${documentEditor.file.id}`,
                        name: documentEditor.file.name || documentEditor.file.key || 'document',
                        ext: getFileExtension(documentEditor.file.name || documentEditor.file.key || ''),
                        file: `${$authHost.defaults.baseURL}/apig/s3/files/${documentEditor.file.id}/stream?token=${encodeURIComponent(auth.user?.access_token)}`
                    }}
                />
            )}
        </Dialog>
    );
};

export default TaskFilesDialog;